import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  convertToArabicHijriDate,
  getDayDate,
  prayerNames,
} from "../utils/prayerUtils";
import {
  formatPrayerClock,
  getPrayerHeroState,
} from "../utils/prayerHeroState";
import usePrayerTimes from "../utils/usePrayerTimes";
import { zones } from "../utils/zonJakim";
import "../styles/Solat.css";

const DEFAULT_LOCATION = { code: "WLY01", name: "Kuala Lumpur, Putrajaya" };
const REQUIRED_PRAYERS = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
const SECONDARY_PRAYERS = ["imsak", "syuruk", "dhuha"];

const readSavedLocation = () => {
  try {
    const saved = localStorage.getItem("selectedLocation");
    return saved ? JSON.parse(saved) : DEFAULT_LOCATION;
  } catch {
    return DEFAULT_LOCATION;
  }
};

const PrayerTime = ({ name, time, isCurrent }) => (
  <div
    className={`home-prayer-time ${isCurrent ? "is-current" : ""}`}
    data-prayer={name}
  >
    <span>{prayerNames[name]?.display}</span>
    <strong className="home-prayer-time-value">{formatPrayerClock(time)}</strong>
  </div>
);

function Solat() {
  const [showSearch, setShowSearch] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(readSavedLocation);
  const [searchValue, setSearchValue] = useState("");
  const [now, setNow] = useState(() => new Date());

  const {
    prayerTimes,
    nextDayPrayerTimes,
    dayDate,
    hijriDate,
  } = usePrayerTimes(selectedLocation.code);

  const heroState = useMemo(
    () => getPrayerHeroState({ prayerTimes, nextDayPrayerTimes, now }),
    [prayerTimes, nextDayPrayerTimes, now]
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!showSearch) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setShowSearch(false);
        setSearchValue("");
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showSearch]);

  const filteredZones = zones
    .map((zone) => ({
      ...zone,
      zones: zone.zones.filter((location) =>
        location.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
    }))
    .filter((zone) => zone.zones.length > 0);

  const selectLocation = (location) => {
    const nextLocation = { code: location.code, name: location.name };
    setSelectedLocation(nextLocation);
    localStorage.setItem("selectedLocation", JSON.stringify(nextLocation));
    setSearchValue("");
    setShowSearch(false);
  };

  const closeSearch = () => {
    setShowSearch(false);
    setSearchValue("");
  };

  const heroPrayer = prayerNames[heroState.prayer];
  const scheduledTimeParts = heroState.scheduledTimePeriod.match(/^(.+?)\s+(AM|PM)$/i);
  const showStatusOrb =
    !["fajr", "maghrib"].includes(heroState.prayer) &&
    !["dawn", "sunset"].includes(heroState.scene);

  return (
    <div className={`home-prayer-shell home-prayer-shell-${heroState.scene}`}>
      <section className={`home-prayer-card home-prayer-scene-${heroState.scene}`}>
        <div className="home-sky-scene" aria-hidden="true">
          <span className="home-sky-cloud home-sky-cloud-one" />
          <span className="home-sky-cloud home-sky-cloud-two" />
          <span className="home-sky-hill home-sky-hill-back" />
          <span className="home-sky-hill home-sky-hill-front" />
        </div>

        <div className="home-prayer-content">
          <div className="home-prayer-meta">
            <div className="home-prayer-date">
              <span>{getDayDate(dayDate).replace(/\s*\|\s*$/, "")}</span>
              {hijriDate && (
                <span className="home-prayer-hijri" lang="ar" dir="rtl">
                  {convertToArabicHijriDate(hijriDate)}
                </span>
              )}
            </div>
          </div>

          <div className="home-next-prayer">
            <div>
              <span className="home-next-label">
                {heroState.mode === "since" ? "Telah masuk waktu" : "Solat seterusnya"}
              </span>
              <h1>
                {heroPrayer?.display || "Waktu solat"}
                {heroPrayer?.arabic && <span lang="ar" dir="rtl">{heroPrayer.arabic}</span>}
              </h1>
            </div>
            <div className="home-prayer-status">
              <div className="home-prayer-clockline">
                {showStatusOrb && (
                  <span
                    className="home-prayer-status-orb"
                    data-scene={heroState.scene}
                    aria-hidden="true"
                  />
                )}
                <strong className="home-prayer-scheduled">
                  {scheduledTimeParts ? (
                    <>
                      {scheduledTimeParts[1]}{" "}
                      <span className="home-prayer-period">{scheduledTimeParts[2]}</span>
                    </>
                  ) : heroState.scheduledTimePeriod}
                </strong>
              </div>
              <div
                className="home-prayer-countdown"
                aria-label={`${heroState.mode === "since" ? "Sejak" : "Lagi"} ${heroState.duration}`}
              >
                <span>{heroState.mode === "since" ? "Sejak" : "Lagi"}</span>
                <strong>{heroState.duration}</strong>
              </div>
            </div>
          </div>

          <div className="home-required-prayers" aria-label="Waktu solat wajib">
            {REQUIRED_PRAYERS.map((name) => (
              <PrayerTime
                key={name}
                name={name}
                time={prayerTimes?.[name]}
                isCurrent={heroState.activePrayer === name}
              />
            ))}
          </div>

          <div className="home-secondary-prayers" aria-label="Waktu tambahan">
            {SECONDARY_PRAYERS.map((name, index) => (
              <React.Fragment key={name}>
                {index > 0 && <span className="home-secondary-dot" aria-hidden="true">•</span>}
                <span>
                  {prayerNames[name]?.display} <strong>{formatPrayerClock(prayerTimes?.[name])}</strong>
                </span>
              </React.Fragment>
            ))}
          </div>

          <button
            type="button"
            className="home-prayer-location"
            onClick={() => setShowSearch(true)}
            aria-expanded={showSearch}
            aria-label={`Tukar lokasi waktu solat. Lokasi semasa: ${selectedLocation.name}`}
            title={selectedLocation.name}
          >
            <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
            <span>{selectedLocation.name}</span>
          </button>
        </div>
      </section>

      {showSearch &&
        createPortal(
          <div
            className="location-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeSearch();
            }}
          >
            <div
              className="location-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="location-modal-title"
            >
              <div className="location-modal-header">
                <div>
                  <span className="location-modal-eyebrow">Waktu solat</span>
                  <h2 id="location-modal-title">Pilih lokasi waktu solat</h2>
                </div>
                <button
                  type="button"
                  onClick={closeSearch}
                  className="close-button"
                  aria-label="Tutup carian lokasi"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <input
                autoFocus
                type="text"
                placeholder="Cari daerah atau negeri"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                className="search-input"
                aria-label="Cari zon waktu solat"
              />

              <div className="zones-list-container show">
                {filteredZones.map((zone) => (
                  <div key={zone.title} className="zone-group">
                    <div className="state-title">{zone.title}</div>
                    <ul className="zone-list">
                      {zone.zones.map((location) => (
                        <li key={location.code}>
                          <button type="button" onClick={() => selectLocation(location)}>
                            {location.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default Solat;
